import { useEffect, useRef, useSyncExternalStore } from "react";

export type NetworkStatus = "online" | "offline" | "slow";

type Subscriber = () => void;

/**
 * Lightweight network-status monitor.
 *
 * 1. Listens to the browser `online`/`offline` events for instant feedback.
 * 2. On-demand ping via `checkNetworkNow()` or `useSpeedTest()` when the
 *    user clicks the wifi icon.
 * 3. Measures round-trip time to classify "slow" connections (> 3 s).
 *
 * No periodic polling — checks only happen on browser events or user action.
 *
 * Uses `useSyncExternalStore` so every consumer shares a single monitor.
 */

let status: NetworkStatus = navigator.onLine ? "online" : "offline";
let pingUrl = ""; // set once via setPingUrl
const listeners = new Set<Subscriber>();

const SLOW_THRESHOLD_MS = 3_000; // > 3 s = slow

function notify() {
  for (const fn of listeners) fn();
}

function setStatus(next: NetworkStatus) {
  if (next !== status) {
    status = next;
    notify();
  }
}

async function ping() {
  if (!pingUrl) return;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10_000);

  try {
    const start = performance.now();
    await fetch(pingUrl, {
      method: "HEAD",
      signal: controller.signal,
      cache: "no-store",
    });
    const elapsed = performance.now() - start;
    setStatus(elapsed > SLOW_THRESHOLD_MS ? "slow" : "online");
  } catch {
    setStatus("offline");
  } finally {
    clearTimeout(timeout);
  }
}

function handleOnline() {
  // Browser says online — verify with a ping
  void ping();
}

function handleOffline() {
  setStatus("offline");
}

function startMonitor() {
  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);
}

function stopMonitor() {
  window.removeEventListener("online", handleOnline);
  window.removeEventListener("offline", handleOffline);
}

let refCount = 0;

function subscribe(cb: Subscriber): () => void {
  listeners.add(cb);
  refCount++;
  if (refCount === 1) startMonitor();
  return () => {
    listeners.delete(cb);
    refCount--;
    if (refCount === 0) stopMonitor();
  };
}

function getSnapshot(): NetworkStatus {
  return status;
}

// ── Public API ──────────────────────────────────────────────

/** Call once at app startup to set the health-check URL. */
export function setPingUrl(url: string) {
  pingUrl = url;
}

/** React hook — returns `"online" | "offline" | "slow"`. */
export function useNetworkStatus(): NetworkStatus {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Trigger an on-demand connectivity check.
 * Returns the measured status after the ping completes.
 */
export async function checkNetworkNow(): Promise<NetworkStatus> {
  await ping();
  return status;
}

/**
 * Hook that returns a `runSpeedTest` callback.
 * The callback pings the server multiple times and returns the average
 * round-trip in milliseconds (or `null` if unreachable).
 */
export function useSpeedTest(): {
  running: boolean;
  latencyMs: number | null;
  runSpeedTest: () => Promise<void>;
} {
  const runningRef = useRef(false);
  const latencyRef = useRef<number | null>(null);
  const subscribersRef = useRef(new Set<Subscriber>());

  // Re-render helpers via useSyncExternalStore
  const subRunning = (cb: Subscriber) => {
    subscribersRef.current.add(cb);
    return () => {
      subscribersRef.current.delete(cb);
    };
  };
  const notifyAll = () => {
    for (const fn of subscribersRef.current) fn();
  };

  const running = useSyncExternalStore(subRunning, () => runningRef.current);
  const latencyMs = useSyncExternalStore(subRunning, () => latencyRef.current);

  useEffect(() => {
    const subs = subscribersRef.current;
    return () => {
      subs.clear();
    };
  }, []);

  async function runSpeedTest() {
    if (!pingUrl || runningRef.current) return;
    runningRef.current = true;
    latencyRef.current = null;
    notifyAll();

    const ROUNDS = 5;
    const times: number[] = [];

    for (let i = 0; i < ROUNDS; i++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10_000);
      try {
        const start = performance.now();
        await fetch(pingUrl, {
          method: "HEAD",
          signal: controller.signal,
          cache: "no-store",
        });
        times.push(performance.now() - start);
      } catch {
        // unreachable round
      } finally {
        clearTimeout(timeout);
      }
    }

    latencyRef.current =
      times.length > 0
        ? Math.round(times.reduce((a, b) => a + b, 0) / times.length)
        : null;
    runningRef.current = false;
    notifyAll();

    // Also update global status based on result
    if (latencyRef.current === null) {
      setStatus("offline");
    } else if (latencyRef.current > SLOW_THRESHOLD_MS) {
      setStatus("slow");
    } else {
      setStatus("online");
    }
  }

  return { running, latencyMs, runSpeedTest };
}
